import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RefreshCw } from "lucide-react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { Navigate } from "react-router-dom";

const CATEGORY_STYLES = {
  bug: "bg-[#F7EFEC] text-[#8C3A30] border-[#E4C9C2]",
  idea: "bg-[#EEF3EC] text-[#3B5B3E] border-[#CFDDCB]",
  other: "bg-[#F2EFEB] text-[#59605C] border-[#EBE6E0]",
};

export default function AdminFeedback() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/admin/feedback");
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (user && user.role !== "admin") {
    return <Navigate to="/app/dashboard" replace />;
  }

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await api.delete(`/admin/feedback/${id}`);
      setItems((prev) => prev.filter((f) => f.id !== id));
      toast.success("Feedback deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  const counts = {
    all: items.length,
    bug: items.filter((i) => i.category === "bug").length,
    idea: items.filter((i) => i.category === "idea").length,
    other: items.filter((i) => i.category === "other").length,
  };

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#8A918D] mb-1">Admin</div>
          <h1 className="font-heading font-bold text-3xl text-[#2C302E]">Feedback inbox</h1>
          <p className="text-sm text-[#59605C] mt-1">
            Submissions from beta testers. Only admins can see this page.
          </p>
        </div>
        <button
          onClick={load}
          className="btn-secondary text-sm"
          data-testid="admin-feedback-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="admin-feedback-filters">
        {["all", "bug", "idea", "other"].map((k) => (
          <button
            key={k}
            data-testid={`admin-feedback-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              filter === k
                ? "bg-[#EEF3EC] border-[#3B5B3E] text-[#3B5B3E]"
                : "bg-white border-[#EBE6E0] text-[#59605C] hover:bg-[#F2EFEB]"
            }`}
          >
            {k.charAt(0).toUpperCase() + k.slice(1)} · {counts[k]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[#8A918D]" data-testid="admin-feedback-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-3xl border border-dashed border-[#EBE6E0] bg-white/60 p-10 text-center"
          data-testid="admin-feedback-empty"
        >
          <div className="font-heading font-semibold text-[#2C302E]">No feedback yet</div>
          <div className="text-sm text-[#8A918D] mt-1">
            Once beta testers submit notes, they&apos;ll show up here.
          </div>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-feedback-list">
          {filtered.map((f) => (
            <li
              key={f.id}
              className="rounded-2xl border border-[#D8CFB9] bg-white p-4"
              data-testid={`admin-feedback-item-${f.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border ${
                        CATEGORY_STYLES[f.category] || CATEGORY_STYLES.other
                      }`}
                    >
                      {f.category}
                    </span>
                    <span className="text-xs text-[#59605C]">
                      {f.user_name ? `${f.user_name} · ` : ""}
                      {f.user_email}
                    </span>
                    <span className="text-xs text-[#8A918D]">· {formatDate(f.created_at)}</span>
                  </div>
                  <p className="text-sm text-[#2C302E] whitespace-pre-wrap break-words">
                    {f.message}
                  </p>
                  {f.page_url && (
                    <div className="mt-2 text-xs text-[#8A918D]">
                      From: <span className="font-mono">{f.page_url}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(f.id)}
                  className="btn-ghost !p-2 text-[#8C3A30]"
                  aria-label="Delete feedback"
                  data-testid={`admin-feedback-delete-${f.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
